import { useState, useMemo } from 'react';
import { useQuery } from '@apollo/react-hooks';
import type { ApolloError } from 'apollo-client';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { getDraftShareIdFromQueryParam } from '@confluence/route-manager/entry-points/getDraftShareIdFromQueryParam';

import { RestrictionsRESTQuery } from './RestrictionsRESTQuery.graphql';
import type { RestrictionsRESTQuery as RestrictionsRESTQueryType } from './__types__/RestrictionsRESTQuery';
import { transformGraphQLQueryToRESTObj } from './restrictionsRESTUtils';
import type { RestrictionsRESTObj } from './restrictionsRESTUtils';

export type UseFetchRestrictionsRESTObjParamsType = {
	contentId: string;
	onSuccess?: (RestrictionsRESTObj) => void;
};
export type UseFetchRestrictionsRESTObjType = {
	fetchedRestrictions: RestrictionsRESTObj | undefined;
	error: Error | ApolloError | undefined;
	refetch: () => void;
};
export const useFetchRestrictionsRESTObj = ({
	contentId,
	onSuccess,
}: UseFetchRestrictionsRESTObjParamsType): UseFetchRestrictionsRESTObjType => {
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const [transformationError, setTransformationError] = useState<Error | undefined>(undefined);
	const { data, error, refetch } = useQuery<RestrictionsRESTQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		RestrictionsRESTQuery,
		{
			variables: {
				contentId,
				draftShareId: getDraftShareIdFromQueryParam(),
			},
			// Without this setting, onCompleted will not fire on refetches
			// See: https://github.com/apollographql/react-apollo/issues/3709#issuecomment-592786578
			notifyOnNetworkStatusChange: true,
			fetchPolicy: 'network-only',
			onCompleted: (completedData) => {
				try {
					onSuccess?.(transformGraphQLQueryToRESTObj(completedData));
					setTransformationError(undefined);
				} catch (restrictionsError) {
					setTransformationError(restrictionsError);
					createAnalyticsEvent({
						type: 'sendUIEvent',
						data: {
							action: 'error',
							actionSubject: 'fetchRestrictions',
							actionSubjectId: 'fetchRestrictionsError',
							source: 'useFetchRestrictionsRESTObj',
							attributes: {
								message: restrictionsError.message,
							},
						},
					}).fire();
				}
			},
		},
	);
	const fetchedRestrictions = useMemo(() => {
		if (data) {
			let transformedData: RestrictionsRESTObj | undefined = undefined;
			try {
				transformedData = transformGraphQLQueryToRESTObj(data);
			} catch (restrictionsError) {
				createAnalyticsEvent({
					type: 'sendUIEvent',
					data: {
						action: 'error',
						actionSubject: 'fetchRestrictions',
						actionSubjectId: 'fetchRestrictionsError',
						source: 'useFetchRestrictionsRESTObj',
						attributes: {
							message: restrictionsError.message,
						},
					},
				}).fire();
			}
			return transformedData;
		} else {
			return undefined;
		}
	}, [data, createAnalyticsEvent]);

	return { fetchedRestrictions, error: error || transformationError, refetch };
};
